//src/components/common/GallerySlider

import React, { useEffect, useRef, useState } from "react";
import api from "../../api/api";

const EFFECTS = [
  "none", "zoom", "blur", "light", "spin", "slice", 
  "flipX", "flipY", "flipDiagonal", "shatter", 
  "explosion", "smoke", "xray", "glitch", "pixelate", 
  "kenburns", "sepia-old", "negative", "noise-heavy", "vignette"
];

const TOTAL_POSITIONS = 13; 
const DEG_PER_STEP = 360 / TOTAL_POSITIONS;

export default function GallerySlider() {
  const [photos, setPhotos] = useState([]);
  const [index, setIndex] = useState(0);
  const [effectIdx, setEffectIdx] = useState(0);
  const [isAuto, setIsAuto] = useState(true);
  const [powerOn, setPowerOn] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const [viewMode, setViewMode] = useState("slider");
  
  // Estado para o Zoom (Lightbox)
  const [previewZoom, setPreviewZoom] = useState(null);

  const timer = useRef(null);

  useEffect(() => {
    api.get("/galleries").then(res => setPhotos(res.data || []));

    api.get("/settings/public").then(res => {
        if (res.data?.settings?.GALLERY_DESIGN_MODE) {
            setViewMode(res.data.settings.GALLERY_DESIGN_MODE);
        }
    });
  }, []);

  useEffect(() => {
    if (photos.length < 2 || !isAuto || (viewMode === "tv" && !powerOn)) return;
    startAuto();
    return stopAuto;
  }, [photos, isAuto, powerOn, viewMode]);

  const startAuto = () => {
    stopAuto();
    timer.current = setInterval(() => {
      handleNext();
    }, 6000);
  };

  const stopAuto = () => timer.current && clearInterval(timer.current);

  const handleNext = () => {
    triggerTransition(() => {
        setIndex(i => (i + 1) % photos.length);
        if (viewMode === "tv") setEffectIdx(Math.floor(Math.random() * EFFECTS.length));
    });
  };

  const handlePrev = () => {
    triggerTransition(() => {
        setIndex(i => (i - 1 + photos.length) % photos.length);
    });
  };

  const triggerTransition = (updateFn) => {
    setIsChanging(true);
    setTimeout(() => {
      updateFn();
      setTimeout(() => setIsChanging(false), 450);
    }, 100);
  };

  if (!photos.length) return null;
  const photo = photos[index];
  const effect = EFFECTS[effectIdx];

  return (
    <div className="w-full max-w-6xl mx-auto p-2 md:p-4 select-none">
      
      {viewMode === "tv" ? (
        /* ======================== DESIGN RETRÔ (TV) ======================== */
        <div className="relative bg-[#2b1e16] p-4 md:p-10 rounded-[2.5rem] md:rounded-[4rem] shadow-[0_40px_80px_rgba(0,0,0,0.9)] border-b-[10px] md:border-b-[20px] border-black/50 animate-fadeIn">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-12 items-center lg:items-stretch">
            <div className="relative flex-[2.5] w-full bg-black rounded-[2rem] md:rounded-[3.5rem] overflow-hidden border-[12px] md:border-[22px] border-[#1a120b] shadow-2xl h-[280px] sm:h-[350px] md:h-[500px]">
               <div className={`absolute inset-0 bg-black z-[70] transition-opacity duration-500 ${powerOn ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} />
              <div className={`relative h-full w-full transition-all duration-700 ${powerOn ? 'scale-100 opacity-100' : 'scale-y-0 opacity-0'}`}>
                {isChanging && <div className="absolute inset-0 z-50 static-noise opacity-70" />}
                <div className="absolute inset-0 w-full h-full">
                  <div className={`absolute inset-0 w-full h-full ${effect}`}>
                    <img 
                      key={index} 
                      src={photo.image_url} 
                      alt="" 
                      className="absolute inset-0 w-full h-full object-cover cursor-zoom-in" 
                      onClick={() => setPreviewZoom(photo.image_url)}
                    />
                  </div>
                </div>
                <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_80px_rgba(0,0,0,0.8)] z-30" />
                <div className="absolute inset-0 scanlines z-40 pointer-events-none opacity-30" />
              </div>
            </div>
            <div className="w-full lg:w-80 flex flex-col bg-[#1a120b] p-6 md:p-10 rounded-3xl md:rounded-[3rem] shadow-inner border border-white/5 justify-between">
               <div className="flex flex-col items-center gap-6">
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-[10px] text-zinc-500 font-black uppercase">Canais</span>
                    <div className="w-24 h-24 rounded-full border-4 border-black relative transition-transform duration-500" style={{ transform: `rotate(${index * DEG_PER_STEP}deg)` }}>
                      <img src="/uploads/icones/bt-radio.png" className="w-full h-full object-cover rounded-full" alt="knob" />
                    </div>
                  </div>
               </div>
               <div className="flex gap-4 w-full mt-8">
                  <button onClick={() => setPowerOn(!powerOn)} className={`flex-1 h-12 rounded-xl border-b-4 border-black ${powerOn ? 'bg-red-600' : 'bg-zinc-800'}`}></button>
                  <button onClick={() => setIsAuto(!isAuto)} className={`flex-1 h-12 rounded-xl border-b-4 border-black text-[10px] font-black ${isAuto ? 'bg-green-600 text-white' : 'bg-zinc-700'}`}>AUTO</button>
               </div>
            </div>
          </div>
        </div>
      ) : (
        /* ======================== DESIGN MODERNO (PREMIUM) ======================== */
        <div className="relative w-full group animate-fadeIn overflow-hidden rounded-3xl shadow-2xl bg-zinc-900 border border-white/5">
          <div className="relative h-[250px] sm:h-[350px] md:h-[420px] w-full overflow-hidden">
            
            <img 
              key={index}
              src={photo.image_url} 
              alt="" 
              onClick={() => setPreviewZoom(photo.image_url)}
              className={`w-full h-full object-cover object-center transition-all duration-[1200ms] ease-out cursor-zoom-in
                ${isChanging ? 'scale-110 blur-sm opacity-30' : 'scale-100 blur-0 opacity-100'}`}
            />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />

            {isAuto && (
              <div className="absolute top-0 left-0 w-full h-1 z-50">
                <div key={index} className="h-full bg-indigo-500 shadow-[0_0_8px_#6366f1] animate-progress-bar" />
              </div>
            )}

            {/* Renderização Condicional do Título e Descrição */}
            {(photo.title || photo.description) && (
              <div className={`absolute bottom-8 left-8 md:left-12 transition-all duration-700 ${isChanging ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                {photo.title && (
                  <h2 className="text-2xl md:text-4xl font-black text-white leading-tight drop-shadow-md uppercase tracking-tight">
                    {photo.title}
                  </h2>
                )}
                {photo.description && (
                  <p className="text-zinc-300 text-xs md:text-sm max-w-sm font-medium line-clamp-1 opacity-80 mt-1">
                    {photo.description}
                  </p>
                )}
              </div>
            )}

            <button 
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/20 backdrop-blur-md border border-white/10 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-indigo-600"
            >
              ❮
            </button>
            <button 
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/20 backdrop-blur-md border border-white/10 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-indigo-600"
            >
              ❯
            </button>

            <div className="absolute bottom-8 right-8 flex items-center gap-2">
              {photos.map((_, i) => (
                <button 
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`transition-all duration-300 rounded-full ${i === index ? 'w-8 h-1.5 bg-indigo-500' : 'w-1.5 h-1.5 bg-white/20'}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL DE ZOOM (LIGHTBOX) ================= */}
      {previewZoom && (
        <div 
          className="fixed inset-0 z-[99999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setPreviewZoom(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white text-3xl hover:scale-110 transition-transform bg-black/50 w-12 h-12 rounded-full flex items-center justify-center"
            onClick={() => setPreviewZoom(null)}
          >
            ✕
          </button>
          <img 
            src={previewZoom} 
            alt="Zoom" 
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl animate-scaleUp object-contain"
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}

      <style>{`
        .static-noise { background: url('/uploads/icones/Color GIF.gif'); background-size: cover; }
        .scanlines { background: linear-gradient(to bottom, rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.3) 50%); background-size: 100% 6px; }

        /* --- 20 EFEITOS --- */
        .zoom img { animation: zoomL 4s infinite alternate; } @keyframes zoomL { from { transform: scale(1); } to { transform: scale(1.2); } }
        .blur img { animation: blurL 3s infinite alternate; } @keyframes blurL { from { filter: blur(0); } to { filter: blur(15px); } }
        .light::before { content: ""; position: absolute; inset: -100%; background: linear-gradient(120deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%); animation: lSweep 2.5s infinite; } @keyframes lSweep { from { transform: translateX(-100%) rotate(10deg); } to { transform: translateX(100%) rotate(10deg); } }
        .spin img { animation: spinL 10s linear infinite; } @keyframes spinL { from { transform: rotate(0) scale(1.3); } to { transform: rotate(360deg) scale(1.3); } }
        .slice { clip-path: inset(0 100% 0 0); animation: sliceL 1.5s forwards infinite alternate; } @keyframes sliceL { to { clip-path: inset(0 0 0 0); } }
        .flipX img { animation: fX 3s infinite alternate; } @keyframes fX { to { transform: perspective(1000px) rotateX(80deg); } }
        .flipY img { animation: fY 3s infinite alternate; } @keyframes fY { to { transform: perspective(1000px) rotateY(80deg); } }
        .flipDiagonal img { animation: fD 3s infinite alternate; } @keyframes fD { to { transform: perspective(1000px) rotate3d(1,1,0,80deg); } }
        .xray { filter: grayscale(1) invert(1) hue-rotate(150deg) brightness(0.7); }
        .xray-bar-loop { position: absolute; top: 0; left: 0; width: 100%; height: 5px; background: #00f2ff; box-shadow: 0 0 20px #00f2ff; animation: xScan 2s linear infinite; } @keyframes xScan { 0% { top: 0; } 100% { top: 100%; } }
        .glitch-overlay-loop { position: absolute; inset: 0; background-size: cover; animation: gAnim 0.3s infinite; mix-blend-mode: hard-light; opacity: 0.5; } @keyframes gAnim { 0% { clip-path: inset(10% 0 20% 0); } 50% { clip-path: inset(25% 0 5% 0); } 100% { clip-path: inset(10% 0 20% 0); } }
        .pixelate { animation: pIn 1.5s steps(8) infinite alternate; } @keyframes pIn { 0% { filter: blur(0); } 100% { filter: blur(20px) contrast(2); } }
        .kenburns img { animation: kbL 12s infinite alternate; } @keyframes kbL { to { transform: scale(1.3) translate(5%, 5%); } }
        .sepia-old { filter: sepia(1) contrast(1.2) brightness(0.9); }
        .negative { filter: invert(1); }
        .vignette { box-shadow: inset 0 0 200px black; }
        .smoke { animation: smokeIn 2.5s forwards; } @keyframes smokeIn { 0% { filter: blur(50px) opacity(0); } 100% { filter: blur(0) opacity(1); } }
        .fragment { animation: assmb 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; } @keyframes assmb { from { opacity: 0; transform: scale(3) translate(var(--tw-translate-x), var(--tw-translate-y)); } to { opacity: 1; transform: scale(1); } }
        .frag-0 { clip-path: polygon(0 0, 50% 0, 50% 50%, 0 50%); --tw-translate-x: -150px; --tw-translate-y: -150px; }
        .frag-1 { clip-path: polygon(50% 0, 100% 0, 100% 50%, 50% 50%); --tw-translate-x: 150px; --tw-translate-y: -150px; }
        .explosion-frag { animation: expl 1.8s forwards; } @keyframes expl { 0% { transform: scale(0) rotate(180deg); opacity: 0; filter: brightness(5); } 100% { transform: scale(1) rotate(0); opacity: 1; filter: brightness(1); } }
      `}</style>
    </div>
  );
}